import React, { useState, useEffect } from "react";
import { Card, Table, Typography, Space, Tag, Descriptions, Divider, Row, Col, Radio, Select, Button, Modal, Form, Input, message, Alert } from "antd";
import { PlusOutlined, DeleteOutlined, EditOutlined, ArrowUpOutlined, ArrowDownOutlined, SortAscendingOutlined, SendOutlined, CheckCircleOutlined } from "@ant-design/icons";
import axios from "axios";
import WorkflowDiagram from "../../purchase-template/components/WorkflowDiagram";
import { Sourcing, SourcingStatus, SourcingType } from "../interface";
import { useCustom, useCustomMutation } from "@refinedev/core";
import { GET_BIDS_BY_SOURCING_QUERY, SUBMIT_RESULTS_MUTATION } from "../queries";
import { notification } from "antd";

// Status order for index-based logic
const SOURCING_STATUS_ORDER = [
  SourcingStatus.DRAFT,
  SourcingStatus.SUBMITTED,
  SourcingStatus.APPROVED,
  SourcingStatus.PUBLISHED,
  SourcingStatus.CLOSED,
  SourcingStatus.REJECTED,
  SourcingStatus.RESULT_SUBMITTED,
  SourcingStatus.AWARD_APPROVED,
  SourcingStatus.PARTIALLY_AWARDED,
  SourcingStatus.AWARDED,
];

const { Text, Title } = Typography;

interface ResultTabProps {
  record: Sourcing;
}

interface BidData {
  _id: string;
  supplierId: string;
  companyName: string;
  items: Array<{
    itemName: string;
    quantity: number;
    unitPrice: number;
    total: number;
    deliveryTime?: string;
  }>;
  compliance?: boolean;
  decision?: string;
  evaluationRemarks?: string;
  evaluationResults?: Array<{
    criteriaId: string;
    value: number;
    remarks?: string;
  }>;
}

export const ResultTab: React.FC<ResultTabProps> = ({ record }) => {
  const [bids, setBids] = useState<BidData[]>([]);
  const [loading, setLoading] = useState(false);

  // Calculate status index for simplified logic
  const statusIndex = SOURCING_STATUS_ORDER.indexOf(record.status);

  const [stepForm] = Form.useForm();
  const [isStepModalVisible, setIsStepModalVisible] = useState(false);
  const [editingStepIndex, setEditingStepIndex] = useState<number | null>(null);
  const [approvalType, setApprovalType] = useState<'LOCAL' | 'EXTERNAL'>('EXTERNAL');
  const [workflows, setWorkflows] = useState<any[]>([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState<any>(null);
  const [steps, setSteps] = useState<any[]>([]);

  // Submit results state
  const [isSubmitModalVisible, setIsSubmitModalVisible] = useState(false);
  const { mutate: submitResults, isLoading: submittingResults } = useCustomMutation();

  // Query for bids - only enable when status is CLOSED
  const { refetch: refetchBids } = useCustom({
    url: '',
    method: 'get',
    meta: {
      gqlQuery: GET_BIDS_BY_SOURCING_QUERY,
      gqlVariables: {
        sourcingId: record?.id || '',
      },
    },
    queryOptions: {
      enabled: !!record?.id && statusIndex >= SOURCING_STATUS_ORDER.indexOf(SourcingStatus.CLOSED),
    },
  });

  useEffect(() => {
    if (statusIndex >= SOURCING_STATUS_ORDER.indexOf(SourcingStatus.CLOSED) && record?.id) {
      loadBidsData();
    }
  }, [statusIndex, record?.id]);

  useEffect(() => {
    if (approvalType === 'EXTERNAL') {
      fetchWorkflows();
    }
  }, [approvalType]);

  const loadBidsData = async () => {
    if (!record?.id || !refetchBids) return;

    setLoading(true);
    try {
      const response = await refetchBids();
      const bidsData = (response as any)?.data?.data?.getBidsBySourcing || [];
      setBids(bidsData);
    } catch (error) {
      console.error('Error loading bids data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkflows = async () => {
    try {
      const res = await axios.get(`${(import.meta as any).env.VITE_USE_FLOW_API_URL}/workflows`, { withCredentials: true });
      setWorkflows(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const addStep = () => {
    stepForm.resetFields();
    setEditingStepIndex(null);
    setIsStepModalVisible(true);
  };

  const editStep = (index: number) => {
    const step = steps[index];
    stepForm.setFieldsValue({
      roleSlug: step.roleSlug,
      stepName: step.stepName,
    });
    setEditingStepIndex(index);
    setIsStepModalVisible(true);
  };

  const saveStep = () => {
    stepForm.validateFields()
      .then(values => {
        let step: any;
        if (editingStepIndex !== null) {
          step = {
            order: steps[editingStepIndex].order,
            roleSlug: values.roleSlug,
            stepName: values.stepName,
          };
          const newSteps = [...steps];
          newSteps[editingStepIndex] = step;
          setSteps(newSteps);
          setEditingStepIndex(null);
        } else {
          const newOrder = (steps.length > 0 ? Math.max(...steps.map(s => s.order)) : 0) + 1;
          step = {
            order: newOrder,
            roleSlug: values.roleSlug,
            stepName: values.stepName,
          };
          setSteps([...steps, step]);
        }
        stepForm.resetFields();
        setIsStepModalVisible(false);
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  const removeStep = (index: number) => {
    const newSteps = [...steps];
    newSteps.splice(index, 1);
    setSteps(newSteps);
  };

  const moveStep = (index: number, dir: -1 | 1) => {
    if (!steps.length) return;
    const target = index + dir;
    if (target < 0 || target >= steps.length) return;
    const newSteps = [...steps];
    const [a, b] = [newSteps[index], newSteps[target]];
    const tmp = a.order;
    a.order = b.order;
    b.order = tmp;
    newSteps[index] = b;
    newSteps[target] = a;
    setSteps(newSteps);
  };

  const sortByOrder = () => {
    if (!steps?.length) return;
    const newSteps = [...steps].sort((x, y) => Number(x.order ?? 0) - Number(y.order ?? 0));
    setSteps(newSteps);
    message.success("Steps sorted by order");
  };

  // Submit results handlers
  const handleSubmitResults = () => {
    setIsSubmitModalVisible(true);
  };

  const handleSubmitResultsConfirm = async () => {
    setIsSubmitModalVisible(false);
    try {
      submitResults(
        {
          url: "",
          method: "post",
          values: {
            input: {
              sourcingId: record.id,
              awardApprovalType: approvalType,
              awardApprovalSteps: approvalType === 'LOCAL' ? steps : undefined,
              awardApprovalFlow: approvalType === 'EXTERNAL' ? selectedWorkflow : undefined,
            },
          },
          meta: { gqlMutation: SUBMIT_RESULTS_MUTATION },
        },
        {
          onSuccess: () => {
            notification.success({
              message: 'Results Submitted Successfully',
              description: 'The sourcing results have been submitted for award approval.',
            });
            // Optionally refresh the page or update the record
            window.location.reload();
          },
          onError: (error: any) => {
            notification.error({
              message: 'Submission Failed',
              description: error.message || 'Failed to submit results.',
            });
          },
        },
      );
    } catch (error) {
      console.error('Submission failed:', error);
    }
  };

  const handleSubmitResultsCancel = () => {
    setIsSubmitModalVisible(false);
  };

  if (statusIndex < SOURCING_STATUS_ORDER.indexOf(SourcingStatus.CLOSED)) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          Results will be available after the sourcing event is closed.
        </div>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          Loading results...
        </div>
      </Card>
    );
  }

  if (bids.length === 0) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          No bids were submitted for this sourcing event.
        </div>
      </Card>
    );
  }

  // Calculate estimate
  const estimate = record.items.reduce((sum, item) => {
    return sum + (item.totalPrice || (item.quantity * item.unitPrice));
  }, 0);

  // Find winners: use itemAwards if available, else fall back to bid-level decision
  const itemAwards = (record as any).itemAwards || [];
  let winnerGroups: { supplierId: string; supplierName: string; total: number; score?: number; items: any[] }[] = [];

  if (itemAwards.length > 0 && itemAwards.some((a: any) => a.decision === 'Winner')) {
    const supplierMap = new Map<string, { supplierId: string; supplierName: string; total: number; items: any[] }>();
    for (const award of itemAwards) {
      if (award.decision === 'Winner') {
        const existing = supplierMap.get(award.supplierId);
        if (existing) {
          existing.total += award.totalPrice;
          existing.items.push(award);
        } else {
          supplierMap.set(award.supplierId, {
            supplierId: award.supplierId,
            supplierName: award.supplierName,
            total: award.totalPrice,
            items: [award],
          });
        }
      }
    }
    winnerGroups = Array.from(supplierMap.values());
  } else {
    // Fallback: bid-level decision
    const winnerBid = bids.find(bid => bid.decision === 'Winner');
    if (winnerBid) {
      const total = winnerBid.items?.reduce((sum: number, item: any) => sum + (item.total || 0), 0) || 0;
      let score = 0;
      if (record.type === SourcingType.RFP && winnerBid.evaluationResults && record.evaluationCriterias) {
        score = record.evaluationCriterias.reduce((sum, criteria) => {
          const evalResult = winnerBid.evaluationResults?.find((er: any) => er.criteriaId === criteria.id);
          const weightedScore = ((evalResult?.value || 0) * criteria.weight) / 100;
          return sum + weightedScore;
        }, 0);
      }
      winnerGroups = [{
        supplierId: winnerBid.supplierId,
        supplierName: winnerBid.companyName,
        total,
        score,
        items: winnerBid.items || [],
      }];
    }
  }

  const hasWinner = winnerGroups.length > 0;
  const winnerBid = bids.find(bid => bid.decision === 'Winner');

  // Calculate winner total (backward compat)
  let winnerTotal = 0;
  let winnerScore = 0;
  let winnerDeliveryTime = 'N/A';

  if (winnerBid) {
    winnerTotal = winnerBid.items.reduce((sum: number, item: any) => sum + item.total, 0);

    if (record.type === SourcingType.RFP && winnerBid.evaluationResults && record.evaluationCriterias) {
      winnerScore = record.evaluationCriterias.reduce((sum, criteria) => {
        const evalResult = winnerBid.evaluationResults?.find(er => er.criteriaId === criteria.id);
        const weightedScore = ((evalResult?.value || 0) * criteria.weight) / 100;
        return sum + weightedScore;
      }, 0);
    }

    const deliveryTimes = winnerBid.items.map(item => item.deliveryTime).filter(Boolean);
    winnerDeliveryTime = deliveryTimes.length > 0 ? deliveryTimes[0]! : 'N/A';
  }

  // Calculate total award amount across all winner groups
  const totalAwardAmount = winnerGroups.reduce((sum, g) => sum + g.total, 0);

  // Calculate variance
  const variance = winnerTotal > 0 && estimate > 0 ? ((winnerTotal - estimate) / estimate) * 100 : 0;
  const varianceText = variance > 0 ? `↑ ${variance.toFixed(1)}%` : variance < 0 ? `↓ ${Math.abs(variance).toFixed(1)}%` : '0%';

  // Prepare bids table data
  const bidsTableData = bids.map(bid => {
    const total = bid.items.reduce((sum, item) => sum + item.total, 0);
    let score = 0;

    if (record.type === SourcingType.RFP && bid.evaluationResults && record.evaluationCriterias) {
      score = record.evaluationCriterias.reduce((sum, criteria) => {
        const evalResult = bid.evaluationResults?.find(er => er.criteriaId === criteria.id);
        const weightedScore = ((evalResult?.value || 0) * criteria.weight) / 100;
        return sum + weightedScore;
      }, 0);
    }

    return {
      supplier: bid.companyName || 'Unknown Supplier',
      totalPrice: record.type === SourcingType.RFQ ? `${record.currency || 'ETB'} ${total.toLocaleString()}` : null,
      score: record.type === SourcingType.RFP ? Math.round(score) : null,
      compliance: bid.compliance ? '✅ Yes' : '❌ No',
      decision: bid.decision === 'Winner' ? '🏆 Winner' : bid.decision === 'Qualified' ? 'Qualified' : 'Disqualified',
      remarks: bid.evaluationRemarks || '',
    };
  });

  const bidsColumns = [
    {
      title: 'Supplier',
      dataIndex: 'supplier',
      key: 'supplier',
    },
    {
      title: record.type === SourcingType.RFQ ? 'Total Price' : 'Score',
      dataIndex: record.type === SourcingType.RFQ ? 'totalPrice' : 'score',
      key: 'totalPriceScore',
      render: (value: string | number) => value,
    },
    {
      title: 'Compliance',
      dataIndex: 'compliance',
      key: 'compliance',
    },
    {
      title: 'Decision',
      dataIndex: 'decision',
      key: 'decision',
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
    },
  ];

  const columns = [
    {
      title: "Order",
      dataIndex: "order",
      width: 90,
    },
    {
      title: "Role Slug",
      dataIndex: "roleSlug",
    },
    {
      title: "Step Name",
      dataIndex: "stepName",
    },
    {
      title: "Actions",
      width: 160,
      render: (_: any, _r: any, index: number) => (
        <Space>
          <Button size="small" icon={<ArrowUpOutlined />} onClick={() => moveStep(index, -1)} />
          <Button size="small" icon={<ArrowDownOutlined />} onClick={() => moveStep(index, +1)} />
          <Button size="small" icon={<EditOutlined />} onClick={() => editStep(index)} />
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeStep(index)} />
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {/* Executive Summary */}
      <Card title="Executive Summary">
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Title level={4}>{record.title || 'Untitled Sourcing'}</Title>
            {record.description && <p>{record.description}</p>}
            <p><strong>Type:</strong> {record.type} | <strong>Scope:</strong> {record.sourcingScope}</p>
          </div>

          <div>
            <strong>Items:</strong>
            <Table
              dataSource={record.items}
              columns={[
                {
                  title: 'Name',
                  dataIndex: 'name',
                  key: 'name',
                },
                {
                  title: 'Description',
                  dataIndex: 'description',
                  key: 'description',
                },
                {
                  title: 'Quantity',
                  dataIndex: 'quantity',
                  key: 'quantity',
                },
                {
                  title: 'Unit',
                  dataIndex: 'unit',
                  key: 'unit',
                  render: (unit: string) => unit || 'Pcs',
                },
                {
                  title: 'Unit Price',
                  dataIndex: 'unitPrice',
                  key: 'unitPrice',
                  render: (price: number) => `${record.currency || 'ETB'} ${price.toLocaleString()}`,
                },
                {
                  title: 'Total Price',
                  dataIndex: 'totalPrice',
                  key: 'totalPrice',
                  render: (total: number) => `${record.currency || 'ETB'} ${(total || 0).toLocaleString()}`,
                },
              ]}
              rowKey="id"
              pagination={false}
              size="small"
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={5}>
                    <strong>Total</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    <strong>
                      {record.currency || 'ETB'} {estimate.toLocaleString()}
                    </strong>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </div>
        </Space>
      </Card>

      {/* Award Groups (Multi-Supplier) */}
      {winnerGroups.length > 0 ? (
        <>
          {winnerGroups.map((group, index) => (
            <Card 
              key={group.supplierId} 
              title={`Award Group ${winnerGroups.length > 1 ? `#${index + 1}` : ''} — ${group.supplierName}`}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Descriptions column={2}>
                  <Descriptions.Item label="Supplier">{group.supplierName}</Descriptions.Item>
                  <Descriptions.Item label="Total Amount">
                    {record.currency || 'ETB'} {group.total.toLocaleString()}
                  </Descriptions.Item>
                  {record.type === SourcingType.RFP && (
                    <Descriptions.Item label="Evaluation Score">{Math.round(group.score || 0)}/100</Descriptions.Item>
                  )}
                </Descriptions>

                {itemAwards.length > 0 ? (
                  <div>
                    <Title level={5}>Awarded Items</Title>
                    <Table
                      dataSource={group.items.map((a: any, i: number) => ({
                        ...a,
                        key: i,
                      }))}
                      columns={[
                        { title: 'Item ID', dataIndex: 'sourcingItemId', key: 'sourcingItemId', render: (id: string) => record.items.find(i => i.id === id)?.name || id },
                        { title: 'Quantity', dataIndex: 'quantity', key: 'quantity' },
                        { title: 'Unit Price', dataIndex: 'unitPrice', key: 'unitPrice', render: (price: number) => `${record.currency || 'ETB'} ${price.toLocaleString()}` },
                        { title: 'Total', dataIndex: 'totalPrice', key: 'totalPrice', render: (total: number) => `${record.currency || 'ETB'} ${total.toLocaleString()}` },
                        { title: 'Decision', dataIndex: 'decision', key: 'decision', render: (d: string) => <Tag color={d === 'Winner' ? 'green' : 'default'}>{d}</Tag> },
                      ]}
                      rowKey="key"
                      pagination={false}
                      size="small"
                      summary={() => (
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={3}>
                            <strong>Total</strong>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1}>
                            <strong>{record.currency || 'ETB'} {group.total.toLocaleString()}</strong>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      )}
                    />
                  </div>
                ) : winnerBid ? (
                  <>
                    <Divider />
                    <div>
                      <Title level={5}>Estimate vs Winner Comparison</Title>
                      <Descriptions column={2} size="small">
                        <Descriptions.Item label="Sourcing Estimate">
                          {record.currency || 'ETB'} {estimate.toLocaleString()}
                        </Descriptions.Item>
                        <Descriptions.Item label="Winner Bid">
                          {record.currency || 'ETB'} {winnerTotal.toLocaleString()}
                        </Descriptions.Item>
                        <Descriptions.Item label="Variance" span={2}>
                          <Text type={variance > 0 ? 'danger' : variance < 0 ? 'success' : 'secondary'}>
                            {varianceText}
                          </Text>
                        </Descriptions.Item>
                      </Descriptions>
                    </div>
                  </>
                ) : null}
              </Space>
            </Card>
          ))}
        </>
      ) : winnerBid ? (
        /* Backward compat: single winner bid display */
        <Card title={`Winner Bid - ${record.currency || 'ETB'} ${winnerTotal.toLocaleString()}`}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Descriptions column={2}>
              <Descriptions.Item label="Supplier">{winnerBid.companyName}</Descriptions.Item>
              <Descriptions.Item label="Delivery Time">{winnerDeliveryTime}</Descriptions.Item>
              <Descriptions.Item label="Total Amount">
                {record.currency || 'ETB'} {winnerTotal.toLocaleString()}
              </Descriptions.Item>
              {record.type === SourcingType.RFP && (
                  <Descriptions.Item label="Evaluation Score">{Math.round(winnerScore || 0)}/100</Descriptions.Item>
              )}
            </Descriptions>
            <Divider />
            <div>
              <Title level={5}>Estimate vs Winner Comparison</Title>
              <Descriptions column={2} size="small">
                <Descriptions.Item label="Sourcing Estimate">
                  {record.currency || 'ETB'} {estimate.toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item label="Winner Bid">
                  {record.currency || 'ETB'} {winnerTotal.toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item label="Variance" span={2}>
                  <Text type={variance > 0 ? 'danger' : variance < 0 ? 'success' : 'secondary'}>
                    {varianceText}
                  </Text>
                </Descriptions.Item>
              </Descriptions>
            </div>
          </Space>
        </Card>
      ) : null}

      {/* Approval Configuration */}
      {statusIndex >= SOURCING_STATUS_ORDER.indexOf(SourcingStatus.RESULT_SUBMITTED) ? (
        // Read-only display after results are submitted
        <Card title="Award Approval Configuration" style={{ marginBottom: 24 }}>
          <Descriptions column={1}>
            <Descriptions.Item label="Approval Type">
              {record.awardApprovalType === 'LOCAL' ? 'Local Approval' : 'External Approval Flow'}
            </Descriptions.Item>
            {record.awardApprovalType === 'EXTERNAL' && record.awardApprovalFlow &&  (
              <Descriptions.Item label="Selected Workflow">
                <div style={{ marginTop: 8 }}>
                  <WorkflowDiagram flow={record.awardApprovalFlow} />
                </div>
              </Descriptions.Item>
            )}
            {record.awardApprovalType === 'LOCAL' && record.awardApprovals && record.awardApprovals.length > 0 && (
              <Descriptions.Item label="Approval Steps">
                <Space wrap style={{ marginTop: 8 }}>
                  {record.awardApprovals.map((approval) => (
                    <Tag key={`${approval.roleSlug}-${approval.order}`} color={approval.stepStatus === 'APPROVED' ? 'green' : approval.stepStatus === 'REJECTED' ? 'red' : 'blue'}>
                      {approval.order}: {approval.stepName || approval.roleSlug} - {approval.stepStatus}
                    </Tag>
                  ))}
                </Space>
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      ) : statusIndex === SOURCING_STATUS_ORDER.indexOf(SourcingStatus.CLOSED) ? (
        // Editable configuration when CLOSED
        <Card title="Approval Configuration" style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="Approval Type" required>
                <Radio.Group
                  value={approvalType}
                  onChange={(e) => setApprovalType(e.target.value)}
                >
                  <Radio value="LOCAL">Local Approval</Radio>
                  <Radio value="EXTERNAL">External Approval Flow</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          {approvalType === 'EXTERNAL' && (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label="Select Workflow" required>
                  <Select
                    value={selectedWorkflow?._id}
                    onChange={(value) => setSelectedWorkflow(workflows.find(w => w._id === value) || selectedWorkflow)}
                    options={[
                      ...(selectedWorkflow && !workflows.find(w => w._id === selectedWorkflow._id) ? [{ value: selectedWorkflow._id, label: selectedWorkflow.name }] : []),
                      ...workflows.map(w => ({ value: w._id, label: w.name }))
                    ]}
                    placeholder="Choose a flow"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          {selectedWorkflow && (
            <Row>
              <Col span={24}>
                <WorkflowDiagram flow={selectedWorkflow} />
              </Col>
            </Row>
          )}

          {approvalType === 'LOCAL' && (
            <Card
              title="Approval Steps"
              extra={
                <Space>
                  <Button icon={<SortAscendingOutlined />} onClick={sortByOrder}>
                    Sort by Order
                  </Button>
                  <Button type="primary" icon={<PlusOutlined />} onClick={addStep}>
                    Add Step
                  </Button>
                </Space>
              }
            >
              <Table
                dataSource={steps}
                columns={columns}
                rowKey={(_r, idx) => String(idx)}
                pagination={false}
                locale={{ emptyText: "No steps added yet" }}
                footer={() => (
                  <div>
                    {steps?.length ? (
                      <Space wrap>
                        {steps.map((s) => (
                          <Tag key={`${s.roleSlug}-${s.order}`} color="blue">
                            {s.order}: {s.stepName || s.roleSlug}
                          </Tag>
                        ))}
                      </Space>
                    ) : (
                      <span style={{ color: "#888" }}>Add steps to define the serial approval flow.</span>
                    )}
                  </div>
                )}
              />
            </Card>
          )}
        </Card>
      ) : null}

      {/* Submit Results Button - Only show when CLOSED */}
      {statusIndex === SOURCING_STATUS_ORDER.indexOf(SourcingStatus.CLOSED) && (
        <Card style={{ marginTop: 24 }}>
          <Row justify="center">
            <Col>
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleSubmitResults}
                disabled={
                  !hasWinner ||
                  (approvalType === 'EXTERNAL' && !selectedWorkflow) ||
                  (approvalType === 'LOCAL' && steps.length === 0) ||
                  submittingResults
                }
                loading={submittingResults}
                size="large"
              >
                Submit Results for Award Approval
              </Button>
            </Col>
          </Row>
        </Card>
      )}

      {/* All Bids */}
      <Card title="All Bids">
        <Table
          dataSource={bidsTableData}
          columns={bidsColumns}
          rowKey={(record, index) => index?.toString() || '0'}
          pagination={false}
          size="small"
        />
      </Card>

      {/* Modal for adding/editing steps */}
      <Modal
        title={editingStepIndex !== null ? "Edit Approval Step" : "Add Approval Step"}
        visible={isStepModalVisible}
        onOk={saveStep}
        onCancel={() => {
          setIsStepModalVisible(false);
          stepForm.resetFields();
          setEditingStepIndex(null);
        }}
        okText={editingStepIndex !== null ? "Update Step" : "Add Step"}
        width={600}
      >
        <Form form={stepForm} layout="vertical">
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="roleSlug"
                label="Role Slug"
                rules={[{ required: true, message: 'Please enter role slug' }]}
              >
                <Input placeholder="e.g. TEAM_LEAD, FINANCE_MANAGER" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="stepName"
                label="Step Name"
              >
                <Input placeholder="Optional display name" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Submit Results Confirmation Modal */}
      <Modal
        title={
          <Space>
            <CheckCircleOutlined style={{ color: '#52c41a' }} />
            Confirm Award Approval Submission
          </Space>
        }
        open={isSubmitModalVisible}
        onOk={handleSubmitResultsConfirm}
        onCancel={handleSubmitResultsCancel}
        okText="Submit for Approval"
        cancelText="Cancel"
        width={800}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <Alert
            message="Results Ready for Submission"
            description="Your sourcing results evaluation is complete and ready for award approval."
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
          />
        </div>

        {/* Award Groups Summary */}
        {winnerGroups.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ marginBottom: 12, fontWeight: 600 }}>Award Recommendations:</h4>
            {winnerGroups.map((group, idx) => (
              <Descriptions key={group.supplierId} column={1} size="small" bordered style={{ marginBottom: 8 }}>
                <Descriptions.Item label="Award Group">
                  <Tag color="green">#{idx + 1}</Tag> {group.supplierName}
                </Descriptions.Item>
                <Descriptions.Item label="Total Amount">{record.currency || 'ETB'} {group.total.toLocaleString()}</Descriptions.Item>
                {record.type === SourcingType.RFP && (
                  <Descriptions.Item label="Evaluation Score">{Math.round(group.score || 0)}/100</Descriptions.Item>
                )}
              </Descriptions>
            ))}
          </div>
        )}

        {/* Flow Diagram */}
        {approvalType === 'EXTERNAL' && selectedWorkflow && (
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ marginBottom: 12, fontWeight: 600 }}>Award Approval Flow:</h4>
            <WorkflowDiagram flow={selectedWorkflow} />
          </div>
        )}

        {approvalType === 'LOCAL' && (
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ marginBottom: 12, fontWeight: 600 }}>Award Approval Flow:</h4>
            <Alert
              message="Local Award Approval Process"
              description="This award will follow the standard local approval workflow."
              type="info"
              showIcon
            />
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <p style={{ marginBottom: 8, fontWeight: 500 }}>
            By clicking "Submit for Approval", you confirm that:
          </p>
          <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
            <li style={{ marginBottom: 4 }}>
              The award recommendation is final and ready for approval
            </li>
            <li style={{ marginBottom: 4 }}>
              The first approver will be notified immediately
            </li>
            <li style={{ marginBottom: 4 }}>
              The sourcing will be marked as results submitted
            </li>
            <li style={{ marginBottom: 0 }}>
              Award approval workflow will be initiated
            </li>
          </ul>
        </div>
      </Modal>
    </Space>
  );
};